import { useState } from "react";
import { Modal, Button } from "flowbite-react";
import { HiTrash, HiX } from "react-icons/hi";
import { useTranslation } from "@repo/core";
import { useStrategies, type Strategy } from "@repo/api";
import { formatPercentage } from "../../../utils/numberFormat";
import { usePortfolioActions } from "../hooks/usePortfolioActions";

interface StrategySlotSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  cloneData: {
    modelId: string;
    universe: string;
    period: string;
  };
}

/**
 * Modal for selecting which strategy to overwrite when user has no empty slots
 */
export const StrategySlotSelectionModal = ({
  isOpen,
  onClose,
  cloneData,
}: StrategySlotSelectionModalProps) => {
  const { t } = useTranslation();
  const { addToOverview, isCloning } = usePortfolioActions();
  const [selectedStrategy, setSelectedStrategy] = useState<Strategy | null>(
    null
  );

  const { data: strategies, isLoading } = useStrategies();

  const handleStrategySelect = (strategy: Strategy) => {
    setSelectedStrategy(strategy);
  };

  const handleConfirm = () => {
    if (selectedStrategy) {
      const payload = {
        ...cloneData,
        deleteModelId: selectedStrategy.modelId,
        deleteSource: true,
      };

      // Show busy indicator
      onClose();
      addToOverview({ payload });
    }
  };

  const handleCancel = () => {
    setSelectedStrategy(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Modal show={isOpen} onClose={handleCancel} size="lg">
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {t("strategySlotSelection.title", "No Empty Slot Available")}
          </h3>
          <button
            type="button"
            className="text-gray-400 hover:text-gray-600"
            onClick={handleCancel}
          >
            <span className="sr-only">{t("common.close", "Close")}</span>
            <HiX className="w-6 h-6" />
          </button>
        </div>

        <div className="mb-6">
          <p className="text-gray-700 leading-relaxed mb-4">
            {t(
              "strategySlotSelection.message",
              "You can have maximum 5 strategies. To add this strategy, please select which existing strategy you want to overwrite:"
            )}
          </p>

          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="font-medium text-blue-900 mb-1">
              {t("strategySlotSelection.newStrategy", "Strategy to Add")}
            </h4>
            <p className="text-sm text-blue-700">
              {t("strategySlotSelection.universe", "Universe")}:{" "}
              {cloneData.universe} |{" "}
              {t("strategySlotSelection.period", "Period")}: {cloneData.period}
            </p>
          </div>
        </div>

        <div className="mb-6">
          <h4 className="font-medium text-gray-900 mb-3">
            {t(
              "strategySlotSelection.selectStrategy",
              "Select Strategy to Overwrite"
            )}
          </h4>

          {isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="h-16 bg-gray-200 rounded-lg"></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {strategies
                ?.filter((s) => !s.modelId.includes("draft"))
                .map((strategy) => (
                  <div
                    key={strategy.modelId}
                    className={`p-4 border rounded-lg cursor-pointer transition-all ${
                      selectedStrategy?.modelId === strategy.modelId
                        ? "border-blue-500 bg-blue-50"
                        : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                    }`}
                    onClick={() => handleStrategySelect(strategy)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h5 className="font-medium text-gray-900">
                            {strategy.name}
                          </h5>
                          {strategy.style && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              {strategy.style}
                            </span>
                          )}
                        </div>
                        <div className="grid grid-cols-3 gap-4 text-sm text-gray-600">
                          <div>
                            <span className="font-medium">CAGR:</span>{" "}
                            <span
                              className={
                                strategy.CAGR && strategy.CAGR >= 0
                                  ? "text-green-600"
                                  : strategy.CAGR
                                    ? "text-red-600"
                                    : "text-gray-400"
                              }
                            >
                              {strategy.CAGR != null
                                ? formatPercentage(strategy.CAGR)
                                : "N/A"}
                            </span>
                          </div>
                          <div>
                            <span className="font-medium">Sharpe:</span>{" "}
                            <span
                              className={
                                strategy.Sharpe && strategy.Sharpe >= 0
                                  ? "text-green-600"
                                  : strategy.Sharpe
                                    ? "text-red-600"
                                    : "text-gray-400"
                              }
                            >
                              {strategy.Sharpe != null
                                ? strategy.Sharpe.toFixed(2)
                                : "N/A"}
                            </span>
                          </div>
                          <div>
                            <span className="font-medium">Volatility:</span>{" "}
                            <span className={strategy.Volatility != null ? "" : "text-gray-400"}>
                              {strategy.Volatility != null
                                ? formatPercentage(strategy.Volatility)
                                : "N/A"}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="flex items-center justify-center w-6 h-6 rounded-full border-2 border-gray-300">
                          {selectedStrategy?.modelId === strategy.modelId && (
                            <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>

        <div className="flex gap-3 justify-end">
          <Button color="gray" onClick={handleCancel}>
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            color="red"
            onClick={handleConfirm}
            disabled={!selectedStrategy || isCloning}
            className="flex items-center gap-2"
          >
            <HiTrash className="w-4 h-4" />
            {isCloning
              ? t("strategySlotSelection.overwriting", "Overwriting...")
              : t(
                  "strategySlotSelection.overwrite",
                  "Overwrite & Add Strategy"
                )}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default StrategySlotSelectionModal;
